import EditIcon from '@mui/icons-material/Edit';
import { Tooltip } from '@mui/material';
import * as React from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Autocomplete, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Form, Formik, getIn } from 'formik';
import * as Yup from "yup"
import { useEditUserMutation } from '../../Slices/roomNateApis';
import { ErrorAlert, SuccessAlert } from '../../utils/Alerts';
import { LoadingButton } from '@mui/lab';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    height: "90%",
    overflowY: "auto",
    width: { xs: '90%', md: "650px" },
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

export default function BasicModal({ items }) {
    const [open, setOpen] = React.useState(false);

    const [updateUser, resultStats] = useEditUserMutation()
    const [load, setLoad] = React.useState(false)
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);
    const [roleConfirmation, setRoleConfirmation] = React.useState('');
    const [roleConfirmationColor, setRoleConfirmationColor] = React.useState('text.secondary');
    const roleConfirmationTimeoutRef = React.useRef(null);


    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("No name provided"),
        email: Yup.string().required("No email provided")
            .matches("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$", "No correct email has been provided")
        ,
    });

    React.useEffect(() => {
        if (resultStats?.isLoading) {
            setLoad(true)
        }

        if (!resultStats?.isLoading) {
            setLoad(false)
        }

        if (resultStats?.isSuccess) {
            setErrorAlert(true)
            setTimeout(() => {
                setErrorAlert(false)
            }, 2000);
        }

        if (resultStats?.isError) {
            setSuccessAlert(true)
            setTimeout(() => {
                setSuccessAlert(false)
            }, 2000);
        }

    }, [resultStats])

    React.useEffect(() => {
        return () => {
            if (roleConfirmationTimeoutRef.current) {
                clearTimeout(roleConfirmationTimeoutRef.current);
            }
        };
    }, []);

    return (
        <>
            {successAlert &&
                <SuccessAlert title="User has been updated successfully!" />
            }

            {errorAlert &&
                <ErrorAlert title="Something Went Wrong!" />
            }

            <Tooltip title="Edit">
                <IconButton color='primary' onClick={handleOpen}>
                    <EditIcon />
                </IconButton>
            </Tooltip>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box position="fixed" top={10} right={0}>
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Formik
                        enableReinitialize={true}
                        initialValues={{
                            name: items.name,
                            email: items.email,
                            status: items.status || 'active',
                            role: items.role || 'user',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {

                            updateUser({ id: items?._id, data: values })
                        }}
                    >
                        {({ values, touched, errors, handleChange, handleBlur, setValues }) => (
                            <Form noValidate autoComplete="off" >
                                <Typography id="modal-modal-title" variant='h6' mb={2}>
                                    Update a user
                                </Typography>

                                <Stack my={2}>
                                    <TextField id="outlined-basic" label="Name" variant="outlined"
                                        name='name'
                                        fullWidth
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.name}
                                        required
                                        helperText={
                                            getIn(touched, "name") && getIn(errors, "name")
                                                ? getIn(errors, "name")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "name") && getIn(errors, "name"))}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <TextField id="outlined-basic" label="Email" variant="outlined"
                                        name='email'
                                        fullWidth
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        required
                                        type={"email"}
                                        helperText={
                                            getIn(touched, "email") && getIn(errors, "email")
                                                ? getIn(errors, "email")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "email") && getIn(errors, "email"))}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <Autocomplete
                                        name='package_id'
                                        fullWidth
                                        disablePortal
                                        value={top100Films.find((item) => item.package_id === values.package_id)}
                                        id="combo-box-demo"
                                        onChange={(e, value) => setValues({ ...values, package_id: value.package_id })}
                                        options={top100Films}
                                        renderInput={(params) => <TextField {...params} label="Package" name="package_id" onBlur={handleBlur} helperText={
                                            getIn(touched, "package_id") && getIn(errors, "package_id")
                                                ? getIn(errors, "package_id")
                                                : ""
                                        }
                                            error={Boolean(getIn(touched, "package_id") && getIn(errors, "package_id"))} />}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <Autocomplete
                                        name='status'
                                        fullWidth
                                        disablePortal
                                        value={{active: "Active", blocked: "In-Active"}[values.status]}
                                        onChange={(e, value) => setValues({ ...values, status: value.value })}
                                        options={[{label: "Active", value: "active"}, {label: "In-Active", value: "blocked"}]}
                                        renderInput={(params) => <TextField {...params} label="Status" name="status" onBlur={handleBlur} helperText={
                                            getIn(touched, "status") && getIn(errors, "status")
                                                ? getIn(errors, "status")
                                                : ""
                                        }
                                            error={Boolean(getIn(touched, "status") && getIn(errors, "status"))} />}
                                    />
                                </Stack>

                                <Stack
                                    my={2}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 2, py: 1 }}
                                >
                                    <Typography variant="body1">Partner Admin</Typography>
                                    <Switch
                                        checked={values.role === 'partnerAdmin'}
                                        onChange={(event) => {
                                            const role = event.target.checked ? 'partnerAdmin' : 'user';
                                            setValues({ ...values, role });
                                            updateUser({ id: items?._id, data: { role: role } });
                                            setRoleConfirmation(
                                                role === 'partnerAdmin'
                                                    ? 'Partner Admin role Activated'
                                                    : 'Partner Admin role De-activated'
                                            );
                                            setRoleConfirmationColor(
                                                role === 'partnerAdmin' ? 'success.main' : 'error.main'
                                            );
                                            if (roleConfirmationTimeoutRef.current) {
                                                clearTimeout(roleConfirmationTimeoutRef.current);
                                            }
                                            roleConfirmationTimeoutRef.current = setTimeout(() => {
                                                setRoleConfirmation('');
                                            }, 3000);
                                        }}
                                    />
                                </Stack>
                                {roleConfirmation && (
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', textAlign: 'right', mt: -1, mb: 1, color: roleConfirmationColor }}
                                    >
                                        {roleConfirmation}
                                    </Typography>
                                )}

                                <Stack mt={2}>
                                    <LoadingButton loading={load} variant="contained" fullWidth type="submit">
                                        Add
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        )}
                    </Formik>


                </Box>
            </Modal >
        </>
    );
}

const top100Films = [
    { label: 'Full furnished package', package_id: 1 },
    { label: 'Launced package', package_id: 2 },
    { label: 'Bath package', package_id: 3 },
    { label: 'Dinning package', package_id: 4 },
];